/**
 * Season-model 2c — `useCreateSeason` hook.
 *
 * Creates a season via `POST /api/seasons` (`handleCreateSeason`,
 * api/index.js). The endpoint deactivates other seasons, inserts a new
 * active one, and returns `{ season }` with the new `id` — OR, if an active
 * season already exists (rapid double-submit / second tab), returns that
 * existing season with `deduped: true` (server-side dup guard). Either way
 * the resolved `season.id` is the EXPLICIT id the caller threads straight
 * into `useGenerateMacroPlan.generate({ seasonId })` — never a refetched
 * (possibly stale) `useActiveSeason` cache.
 *
 * Mirrors `useGenerateMacroPlan`'s contract: `create()` never throws;
 * failures surface via `error` / `lastError`. An in-flight ref makes a
 * second concurrent call a no-op (the CTA's pending-disable is the primary
 * guard; this backs it at the hook layer).
 */

import { useCallback, useRef, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';
import type { ActiveSeason } from './useActiveSeason';
import type { CodedError } from '../lib/roleRefusal';

export interface CreateSeasonInput {
  name: string;
  /** YYYY-MM-DD (provisional — 2a reconciles to the real macro span). */
  startDate: string;
  /** YYYY-MM-DD. */
  endDate: string;
  sport?: string | null;
  teamId?: string | null;
}

export interface UseCreateSeasonResult {
  /**
   * Create (or return the existing active) season. Resolves to:
   *   - `{ ok: true,  season, deduped }` — the created OR existing active
   *     season; `deduped: true` means the server returned an EXISTING
   *     active season instead of creating (SR-2: roll-forward callers must
   *     check this — generating into a deduped season would re-anchor it)
   *   - `{ ok: false, error }`  — on failure / already-in-flight
   * Never throws.
   */
  create: (
    input: CreateSeasonInput,
  ) => Promise<{ ok: boolean; season?: ActiveSeason; deduped?: boolean; error?: Error }>;
  /** True while a create request is in flight. Use to disable the CTA. */
  creating: boolean;
  /** Latest in-flight error, cleared at the start of each create. */
  error: Error | null;
  /** Persists across attempts so the route can render retry copy. */
  lastError: Error | null;
}

export function useCreateSeason(): UseCreateSeasonResult {
  const fetchAuthed = useAuthFetch();
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const inFlightRef = useRef<boolean>(false);

  const create = useCallback(
    async (input: CreateSeasonInput) => {
      if (inFlightRef.current) {
        // Defensive: a second concurrent call is a no-op (CTA disable is the
        // primary guard). Don't set lastError — this isn't a user-facing fail.
        return { ok: false as const, error: new Error('Season creation already in progress') };
      }
      inFlightRef.current = true;
      setCreating(true);
      setError(null);
      try {
        const res = await fetchAuthed('/api/seasons', {
          method: 'POST',
          body: JSON.stringify(input),
        });
        if (!res.ok) {
          let msg = `/api/seasons responded ${res.status}`;
          let code: string | undefined;
          try {
            const body = await res.json();
            if (body && typeof body.error === 'string') msg = body.error;
            // UAT-B4 — surface the role-refusal code so the route maps it to
            // the refusal state 1e (every other error is unchanged).
            if (body && typeof body.code === 'string') code = body.code;
          } catch {
            /* ignore body parse errors */
          }
          const err: CodedError = new Error(msg);
          if (code) err.code = code;
          throw err;
        }
        const json = (await res.json()) as { season?: ActiveSeason; deduped?: boolean };
        if (!json.season?.id) {
          throw new Error('Season created but no id returned');
        }
        return { ok: true as const, season: json.season, deduped: json.deduped === true };
      } catch (e) {
        const err = e instanceof Error ? e : new Error(typeof e === 'string' ? e : 'Create failed');
        setError(err);
        setLastError(err);
        return { ok: false as const, error: err };
      } finally {
        inFlightRef.current = false;
        setCreating(false);
      }
    },
    [fetchAuthed],
  );

  return { create, creating, error, lastError };
}
